import { useCallback } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import { useAppStore } from '../store/appStore';
import { Fonts, MdiIcons } from '../styles/fonts';
import { useTheme, DarkColors } from '../styles/theme';
import { Tooltip } from './Tooltip';

const DESKTOP_BREAKPOINT = 768;

export default function TopBar() {
  const appTitle = useAppStore((s) => s.appTitle);
  const viewMode = useAppStore((s) => s.settings.view_mode);
  const sortReverse = useAppStore((s) => s.settings.sort_chapters_reverse);
  const leftOpen = useAppStore((s) => s.leftDrawerOpen);
  const rightOpen = useAppStore((s) => s.rightDrawerOpen);
  const toggleLeftDrawer = useAppStore((s) => s.toggleLeftDrawer);
  const toggleRightDrawer = useAppStore((s) => s.toggleRightDrawer);
  const closeDrawers = useAppStore((s) => s.closeDrawers);
  const setSettings = useAppStore((s) => s.setSettings);
  const setCardInput = useAppStore((s) => s.setCardInput);

  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const isMobile = width <= DESKTOP_BREAKPOINT;
  const isMushaf = viewMode.value === 'mushaf';

  const scrollToTop = useCallback(() => {
    if (Platform.OS === 'web') {
      const scrollPane = document.getElementById('scroll-center-pane-helper');
      if (scrollPane) scrollPane.scrollTop = 0;
    }
  }, []);

  const handleSearchPress = useCallback(() => {
    setCardInput('search', '');
    if (isMobile) toggleLeftDrawer();
  }, [isMobile, toggleLeftDrawer, setCardInput]);

  const handleSortPress = useCallback(() => {
    setSettings({ sort_chapters_reverse: !sortReverse });
  }, [sortReverse, setSettings]);

  const iconColor = isMushaf ? styles.mushafIcon : { color: colors.topBarIcon };

  return (
    <View
      nativeID="top-bar"
      style={[
        styles.topBar,
        isMushaf ? styles.topBarMushaf : { backgroundColor: colors.topBarBg },
      ]}
    >
      <View style={styles.leftActions}>
        {isMobile ? (
          <Pressable
            nativeID="toggle-left-pane"
            style={styles.topbarBtn}
            accessibilityLabel={leftOpen ? 'Back' : 'Surah list'}
            onPress={leftOpen ? closeDrawers : toggleLeftDrawer}
          >
            <Text style={[styles.icon, iconColor]}>
              {leftOpen ? MdiIcons.close : MdiIcons.menu}
            </Text>
          </Pressable>
        ) : (
          <>
            <Tooltip title="Search (s)">
              <Pressable style={styles.topbarBtn} accessibilityLabel="Search" onPress={handleSearchPress}>
                <Text style={[styles.icon, iconColor]}>{MdiIcons.search}</Text>
              </Pressable>
            </Tooltip>
            <Tooltip title="Change surah order">
              <Pressable style={styles.topbarBtn} accessibilityLabel="Sort chapters" onPress={handleSortPress}>
                <Text style={[styles.icon, iconColor, sortReverse && styles.iconActive]}>{MdiIcons.sort}</Text>
              </Pressable>
            </Tooltip>
          </>
        )}
      </View>

      <Pressable style={styles.appHeader} onPress={scrollToTop}>
        <Text style={[styles.logo, iconColor]}>Guided Verses</Text>
        {appTitle ? (
          <Text nativeID="app-title" style={[styles.appTitle, iconColor]}>
            {appTitle}
          </Text>
        ) : null}
      </Pressable>

      <View style={styles.rightActions}>
        <Tooltip title="Settings">
          <Pressable
            nativeID="settings-btn"
            style={styles.topbarBtn}
            accessibilityLabel="Settings"
            onPress={rightOpen ? closeDrawers : toggleRightDrawer}
          >
            <Text style={[styles.icon, iconColor]}>{rightOpen ? MdiIcons.close : MdiIcons.settings}</Text>
          </Pressable>
        </Tooltip>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  topBar: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
    zIndex: 1100,
    // @ts-expect-error web shadow
    boxShadow: '0 2px 5px rgba(0,0,0,0.26)',
  },
  topBarMushaf: {
    backgroundColor: 'rgba(45, 45, 45, 0.6)',
    // @ts-expect-error web backdrop-filter
    backdropFilter: 'blur(12px) saturate(80%)',
    WebkitBackdropFilter: 'blur(12px) saturate(80%)',
    boxShadow: 'none',
  },
  mushafIcon: {
    color: DarkColors.topBarIcon,
  },
  leftActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  topbarBtn: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    fontFamily: Fonts.MDI,
    fontSize: 24,
  },
  iconActive: {
    opacity: 0.7,
  },
  appHeader: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    fontSize: 18,
    fontWeight: '700',
  },
  appTitle: {
    fontSize: 11,
    marginTop: 2,
  },
});
